import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const GREEN = '#00FF41';
const GOLD = '#FFD700';
const CYAN = '#00CCFF';
const RED = '#FF3B30';

const statusColor = (s) => {
  if (s === 'Exploding') return RED;
  if (s === 'Busy') return GOLD;
  if (s === 'Moderate') return CYAN;
  return GREEN;
};

/**
 * Live venue detail popover for the God's Eye.
 * Uses GET /api/admin/venues/{id} — buzz gauge 0-100, hourly bars, trend arrow.
 */
export default function AdminVenueDetailModal({ venueId, adminKey, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !venueId || !adminKey) return;
    setLoading(true);
    axios
      .get(`${API}/admin/venues/${venueId}?admin_key=${adminKey}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, venueId, adminKey]);

  const score = data?.buzz_score ?? 0;
  const status = data?.buzz_status ?? '—';
  const color = statusColor(status);
  const peakHours = (data?.hourly || []).slice(18).concat((data?.hourly || []).slice(0, 4));
  const peakLabels = ['6P','7P','8P','9P','10P','11P','12A','1A','2A','3A'];
  const peakMax = Math.max(...peakHours, 1);
  const TrendIcon = data?.trend === 'up' ? TrendingUp : data?.trend === 'down' ? TrendingDown : Minus;
  const trendColor = data?.trend === 'up' ? GREEN : data?.trend === 'down' ? RED : '#999';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg bg-black border-2 text-white"
        style={{ borderColor: `${GREEN}66`, boxShadow: `0 0 30px ${GREEN}55` }}
        data-testid="venue-detail-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-black tracking-widest" style={{ fontFamily: 'Orbitron, sans-serif', color: GREEN, textShadow: `0 0 6px ${GREEN}` }}>
            {loading ? 'LOADING…' : (data?.name || 'VENUE')}
          </DialogTitle>
          {data?.address && <p className="text-xs font-mono text-emerald-300/70">{data.address}</p>}
        </DialogHeader>

        {data && (
          <div className="space-y-4">
            {/* Buzz Gauge 0-100 */}
            <div className="rounded-lg border p-3" style={{ borderColor: `${color}66`, background: 'rgba(0,0,0,0.6)' }}>
              <p className="text-[10px] font-mono tracking-widest" style={{ color }}>◆ BUZZ SCORE</p>
              <div className="flex items-baseline gap-3 mt-1">
                <p className="text-5xl font-black leading-none" style={{ color, fontFamily: 'Orbitron, sans-serif', textShadow: `0 0 12px ${color}88` }}>
                  {score}
                </p>
                <span className="text-xs font-mono uppercase tracking-widest" style={{ color }}>{status}</span>
                <TrendIcon className="w-5 h-5 ml-auto" style={{ color: trendColor }} />
              </div>
              <div className="h-2 rounded-full mt-3 overflow-hidden" style={{ background: '#0a0a0a' }}>
                <div className="h-full transition-all" style={{ width: `${score}%`, background: `linear-gradient(90deg, ${GREEN}, ${GOLD}, ${RED})`, boxShadow: `0 0 8px ${color}` }} />
              </div>
              <div className="flex justify-between text-[9px] font-mono text-zinc-500 mt-1">
                <span>0 Quiet</span><span>50 Moderate</span><span>100 Exploding</span>
              </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg border p-2" style={{ borderColor: `${CYAN}55`, background: 'rgba(0,0,0,0.5)' }}>
                <p className="text-[9px] font-mono tracking-widest" style={{ color: CYAN }}>ACTIVE USERS</p>
                <p className="text-2xl font-black text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>{data.active_users}</p>
              </div>
              <div className="rounded-lg border p-2" style={{ borderColor: `${GREEN}55`, background: 'rgba(0,0,0,0.5)' }}>
                <p className="text-[9px] font-mono tracking-widest" style={{ color: GREEN }}>CHECK-INS TODAY</p>
                <p className="text-2xl font-black text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>{data.checkins_today}</p>
              </div>
              <div className="rounded-lg border p-2" style={{ borderColor: `${GOLD}55`, background: 'rgba(0,0,0,0.5)' }}>
                <p className="text-[9px] font-mono tracking-widest" style={{ color: GOLD }}>PEAK HOUR</p>
                <p className="text-2xl font-black text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  {data.peak_hour !== null && data.peak_hour !== undefined ? `${((data.peak_hour + 11) % 12) + 1}${data.peak_hour < 12 ? 'A' : 'P'}` : '—'}
                </p>
              </div>
            </div>

            {/* Hourly bars */}
            <div className="rounded-lg border p-3" style={{ borderColor: `${GOLD}55`, background: 'rgba(0,0,0,0.5)' }}>
              <p className="text-[10px] font-mono tracking-widest mb-2" style={{ color: GOLD }}>◆ TONIGHT'S FLOW</p>
              <div className="flex items-end justify-between h-24 gap-1">
                {peakHours.map((v, i) => (
                  <div key={i} className="flex flex-col items-center gap-1 flex-1 h-full justify-end">
                    <div className="w-full rounded-t" style={{ height: `${(v / peakMax) * 100}%`, minHeight: v > 0 ? 4 : 0, background: `linear-gradient(180deg, ${GOLD}, #B8860B)`, boxShadow: v > 0 ? `0 0 6px ${GOLD}88` : 'none' }} />
                    <span className="text-[8px] font-mono" style={{ color: GOLD }}>{peakLabels[i]}</span>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-[9px] font-mono text-zinc-500 text-right">Last update: {new Date(data.last_update).toLocaleTimeString()}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
